import { FlexLayout } from '@eduact/ed-system'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import TrustedDevicesApi from '../Settings.api'
import Modal from '@Components/Utilities/Popup/Modal'
import DeviceCard from './DeviceCard'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@Hooks/ReduxHooks'

/**
 * الأجهزة المسجّلة — the design's devices list: usage card with the
 * logout-all CTA, then one row per device. Same TrustedDevicesApi calls,
 * clientjs current-device detection, and confirm modal as before.
 */
const Devices = () => {
	const [t] = useTranslation('settings')
	const [devices, setDevices] = useState<Array<Device>>([])
	const [openModel, setOpenModel] = useState<boolean>(false)
	const {
		classroomsSections: { device_limit },
	} = useAppSelector(state => state.settings)

	const getAllDevices = async () => {
		const {
			data: { data },
		} = await TrustedDevicesApi.getAllDevices()
		setDevices(data)
	}

	const logoutDevices = async () => {
		await TrustedDevicesApi.logoutAllDevices()
			.then(() => {
				getAllDevices()
			})
			.then(() => {
				setOpenModel(false)
			})
	}

	useEffect(() => {
		getAllDevices()
	}, [])

	return (
		<>
			<ModelStyle modalOpen={openModel}>
				<Modal.Content
					minWidth={'fit-content'}
					minHeight={'fit-content'}
					height="17.994rem"
					onClose={() => {
						setOpenModel(false)
					}}
				>
					<ContentWrapper justifyContent="space-between" alignItems="center" flexDirection="column" height="100%">
						<FlexLayout
							p={{ lg: '3.688rem', sm: '2.688rem' }}
							pb={{ lg: '0', sm: '0' }}
							flexDirection="column"
							alignItems="flex-start"
							gridGap="0.875rem"
						>
							<Text>{t('text')}</Text>
							<Note>{t('note')}</Note>
						</FlexLayout>
						<FlexLayout
							justifyContent="space-between"
							alignItems="center"
							width="100%"
							p={{ lg: '0 2.813rem 1.8rem', sm: '0 1.688rem 1.5rem' }}
						>
							<DialogButton
								data-tone="muted"
								onClick={() => {
									setOpenModel(false)
								}}
							>
								{t('discard')}
							</DialogButton>
							<DialogButton
								data-tone="danger"
								onClick={() => {
									logoutDevices()
								}}
							>
								{t('confirm')}
							</DialogButton>
						</FlexLayout>
					</ContentWrapper>
				</Modal.Content>
			</ModelStyle>

			{/* Usage card + logout-all CTA */}
			<UsageCard>
				<div className="line">
					{t('currentlyUsing')} <b className="count">{devices.length}</b> {t('outOff')} <b className="limit">{device_limit}</b>{' '}
					{t('allowed_devices', { defaultValue: 'جهاز مسموح.' })}
				</div>
				<LogoutAllButton type="button" onClick={() => setOpenModel(true)}>
					{t('logoutAllDevices')}
				</LogoutAllButton>
			</UsageCard>

			{devices.map((device, index) => (
				<DeviceCard
					isCurrentDevice={device.current}
					device={device}
					key={`${device.device_id}-${index}`}
					setDevices={setDevices}
				/>
			))}
		</>
	)
}

const ModelStyle = styled(Modal)`
	display: flex;
	flex-direction: column;
	background-color: #fff;
	color: rgba(0, 0, 0, 0.87);
	border-radius: 4px;
	box-shadow: 0px 11px 15px -7px rgb(0 0 0 / 20%), 0px 24px 38px 3px rgb(0 0 0 / 14%), 0px 9px 46px 8px rgb(0 0 0 / 12%);
	margin: 32px;
	position: relative;
	overflow-y: auto;
`
const UsageCard = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	.line {
		font: var(--fw-regular) 13px/1.7 var(--font-sans);
		color: var(--text-secondary);
		text-align: start;
	}
	.count {
		color: var(--blue);
	}
	.limit {
		color: var(--text-primary);
	}
`
const LogoutAllButton = styled.button`
	height: 48px;
	border: none;
	border-radius: var(--r-md);
	background: var(--blue);
	color: #fff;
	font: var(--fw-bold) 14px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	transition: background var(--dur-fast) var(--ease);
	&:hover {
		background: var(--blue-hover);
	}
`
const DialogButton = styled.button`
	height: 38px;
	padding: 0 18px;
	border: none;
	border-radius: var(--r-md);
	font: var(--fw-bold) 13px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	&[data-tone='muted'] {
		background: var(--surface-sunken-strong);
		color: var(--text-primary);
	}
	&[data-tone='danger'] {
		background: var(--error);
		color: #fff;
	}
`
const ContentWrapper = styled(FlexLayout)`
	font-family: var(--font-sans);
	gap: 1rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		gap: 1.938rem;
	}`}
`
const Text = styled.p`
	font-size: 0.875rem;
	max-width: 33.25rem;
	line-height: 1.5;
	${({ theme }) => `${theme.mediaQueries.large}{
		font-size: 1.125rem;
		max-width: 28.25rem;
	}`}
`
const Note = styled.p`
	font-size: 1rem;
	font-weight: 600;
	color: var(--text-primary);
`
export default Devices
